/** Curated 2024 persistent spell mechanics. These are deliberately separate from
 * instant save-damage definitions: Web does not deal damage or save on casting. */
export const WEB_DEFINITION = Object.freeze({
  id: 'spell:web', name: 'Web', rulesVersion: '2024', level: 2,
  targeting: Object.freeze({ shape: 'cube', sizeFeet: 20, rangeFeet: 60 }),
  durationSeconds: 3600, requiresConcentration: true,
  terrain: Object.freeze({ difficultTerrain: true, obscurement: 'light' }),
  save: Object.freeze({ ability: 'dexterity', triggers: Object.freeze(['first-entry-on-turn', 'start-of-target-turn']), failureCondition: 'condition:restrained' }),
  escape: Object.freeze({ actionCost: 'action', ability: 'strength', skill: 'athletics' }),
  unsupportedAnchorExpiration: Object.freeze({ type: 'sourceTurn', boundary: 'start', remainingOccurrences: 1 }),
  ignition: Object.freeze({ cubeFeet: 5, rounds: 1, startOfTurnDamage: '2d4', damageType: 'fire' }),
});

/** Build the root consumed by the shared lifecycle. Actual cast preparation must
 * validate map placement, spell access, DC, slot, and action on the server. */
export const createWebEffect = ({ id, sourceCombatantId, dc, placement, round, turnSequence, supported = true }) => {
  if (!id || !sourceCombatantId || !Number.isInteger(dc) || dc < 1 ||
      !Number.isInteger(round) || round < 1 || !Number.isInteger(turnSequence) || turnSequence < 0 ||
      !placement?.mapId || !Number.isFinite(placement.point?.x) || !Number.isFinite(placement.point?.y) ||
      placement.point.x < 0 || placement.point.x > 1 || placement.point.y < 0 || placement.point.y > 1 ||
      typeof supported !== 'boolean') throw new Error('Invalid persistent Web effect.');
  return {
    id, definitionId: WEB_DEFINITION.id, kind: 'persistent-area', name: 'Web',
    sourceCombatantId, targetCombatantId: sourceCombatantId, concentrationId: id,
    placement: { mapId: placement.mapId, point: { x: placement.point.x, y: placement.point.y } },
    area: { ...WEB_DEFINITION.targeting }, save: { ...WEB_DEFINITION.save, dc },
    terrain: { ...WEB_DEFINITION.terrain }, escape: { ...WEB_DEFINITION.escape, dc },
    expiration: supported ? { type: 'rounds', expiresAtRound: round + 600, boundary: 'start' }
      : { ...WEB_DEFINITION.unsupportedAnchorExpiration, combatantId: sourceCombatantId },
    appliedAtRound: round, appliedAtTurnSequence: turnSequence,
  };
};

/** Revised Moonbeam saves on appearance, entry/movement and target turn END. */
export const MOONBEAM_DEFINITION = Object.freeze({
  id: 'spell:moonbeam', name: 'Moonbeam', rulesVersion: '2024', level: 2,
  targeting: Object.freeze({ shape: 'cylinder', radiusFeet: 5, heightFeet: 40, rangeFeet: 120 }),
  durationSeconds: 60, requiresConcentration: true,
  save: Object.freeze({ ability: 'constitution', triggers: Object.freeze(['appearance', 'first-entry-on-turn', 'area-movement', 'end-of-target-turn']) }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '2d10', type: 'radiant' }]) }),
  movement: Object.freeze({ actionCost: 'action', distanceFeet: 60, laterTurnsOnly: true }),
});

export const createMoonbeamEffect = (input) => {
  const base = createWebEffect({ ...input, supported: true });
  if (!Number.isInteger(input.castLevel) || input.castLevel < 2 || input.castLevel > 9) throw new Error('Invalid Moonbeam slot level.');
  const { terrain, escape, ...root } = base;
  return { ...root, definitionId: MOONBEAM_DEFINITION.id, name: MOONBEAM_DEFINITION.name,
    area: { ...MOONBEAM_DEFINITION.targeting }, save: { ...MOONBEAM_DEFINITION.save, dc: input.dc },
    damage: { formula: `${input.castLevel}d10`, type: 'radiant' }, movement: { ...MOONBEAM_DEFINITION.movement },
    expiration: { type: 'rounds', expiresAtRound: input.round + 10, boundary: 'start' } };
};

/**
 * Revised Flaming Sphere: a Dexterity save for a creature that ends its turn within 5 feet of it, or that it's rammed into.
 * It moves by Bonus Action, on the turn it's cast too. The area is the sphere's square and those around it.
 */
export const FLAMING_SPHERE_DEFINITION = Object.freeze({
  id: 'spell:flaming-sphere', name: 'Flaming Sphere', rulesVersion: '2024', level: 2,
  targeting: Object.freeze({ shape: 'sphere', radiusFeet: 5, rangeFeet: 60 }),
  durationSeconds: 60, requiresConcentration: true,
  save: Object.freeze({ ability: 'dexterity', triggers: Object.freeze(['area-movement', 'end-of-target-turn']) }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '2d6', type: 'fire' }]) }),
  movement: Object.freeze({ actionCost: 'bonusAction', distanceFeet: 30 }),
});
export const createFlamingSphereEffect = (input) => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 2 || input.castLevel > 9) throw new Error('Invalid Flaming Sphere slot level.');
  const base = createMoonbeamEffect(input);
  return { ...base, definitionId: FLAMING_SPHERE_DEFINITION.id, name: FLAMING_SPHERE_DEFINITION.name,
    area: { ...FLAMING_SPHERE_DEFINITION.targeting }, save: { ...FLAMING_SPHERE_DEFINITION.save, dc: input.dc },
    damage: { formula: `${input.castLevel}d6`, type: 'fire' }, movement: { ...FLAMING_SPHERE_DEFINITION.movement } };
};

/** Revised Cloudkill shares damage triggers, but drifts automatically. */
export const CLOUDKILL_DEFINITION = Object.freeze({
  id: 'spell:cloudkill', name: 'Cloudkill', rulesVersion: '2024', level: 5,
  targeting: Object.freeze({ shape: 'sphere', radiusFeet: 20, rangeFeet: 120 }),
  durationSeconds: 600, requiresConcentration: true,
  terrain: Object.freeze({ obscurement: 'heavy' }),
  save: MOONBEAM_DEFINITION.save,
  damage: Object.freeze({ components: Object.freeze([{ formula: '5d8', type: 'poison' }]) }),
  movement: Object.freeze({ automatic: true, distanceFeet: 10, boundary: 'start-of-source-turn' }),
});
export const createCloudkillEffect = input => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 5 || input.castLevel > 9) throw new Error('Invalid Cloudkill slot level.');
  const base = createMoonbeamEffect(input);
  return { ...base, definitionId: CLOUDKILL_DEFINITION.id, name: CLOUDKILL_DEFINITION.name,
    area: { ...CLOUDKILL_DEFINITION.targeting }, terrain: { ...CLOUDKILL_DEFINITION.terrain },
    damage: { formula: `${input.castLevel}d8`, type: 'poison' }, movement: { ...CLOUDKILL_DEFINITION.movement },
    expiration: { type: 'rounds', expiresAtRound: input.round + 100, boundary: 'start' } };
};

/** Incendiary Cloud drifts like Cloudkill: Dexterity, Fire, and no higher-level dice. */
export const INCENDIARY_CLOUD_DEFINITION = Object.freeze({
  id: 'spell:incendiary-cloud', name: 'Incendiary Cloud', rulesVersion: '2024', level: 8,
  targeting: Object.freeze({ shape: 'sphere', radiusFeet: 20, rangeFeet: 150 }),
  durationSeconds: 60, requiresConcentration: true,
  terrain: Object.freeze({ obscurement: 'heavy' }),
  save: Object.freeze({ ability: 'dexterity', triggers: MOONBEAM_DEFINITION.save.triggers }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '10d8', type: 'fire' }]) }),
  movement: Object.freeze({ automatic: true, distanceFeet: 10, boundary: 'start-of-source-turn' }),
});
export const createIncendiaryCloudEffect = input => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 8 || input.castLevel > 9) throw new Error('Invalid Incendiary Cloud slot level.');
  const base = createMoonbeamEffect(input);
  return { ...base, definitionId: INCENDIARY_CLOUD_DEFINITION.id, name: INCENDIARY_CLOUD_DEFINITION.name,
    area: { ...INCENDIARY_CLOUD_DEFINITION.targeting }, terrain: { ...INCENDIARY_CLOUD_DEFINITION.terrain }, save: { ...INCENDIARY_CLOUD_DEFINITION.save, dc: input.dc },
    damage: { formula: '10d8', type: 'fire' }, movement: { ...INCENDIARY_CLOUD_DEFINITION.movement },
    expiration: { type: 'rounds', expiresAtRound: input.round + 10, boundary: 'start' } };
};

/** Insect Plague is stationary; no area-movement trigger or movement action. */
export const INSECT_PLAGUE_DEFINITION = Object.freeze({
  id: 'spell:insect-plague', name: 'Insect Plague', rulesVersion: '2024', level: 5,
  targeting: Object.freeze({ shape: 'sphere', radiusFeet: 20, rangeFeet: 300 }),
  durationSeconds: 600, requiresConcentration: true,
  terrain: Object.freeze({ difficultTerrain: true, obscurement: 'light' }),
  save: Object.freeze({ ability: 'constitution', triggers: Object.freeze(['appearance', 'first-entry-on-turn', 'end-of-target-turn']) }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '4d10', type: 'piercing' }]) }),
});
export const createInsectPlagueEffect = input => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 5 || input.castLevel > 9) throw new Error('Invalid Insect Plague slot level.');
  const { movement, ...base } = createMoonbeamEffect(input);
  return { ...base, definitionId: INSECT_PLAGUE_DEFINITION.id, name: INSECT_PLAGUE_DEFINITION.name,
    area: { ...INSECT_PLAGUE_DEFINITION.targeting }, save: { ...INSECT_PLAGUE_DEFINITION.save, dc: input.dc },
    terrain: { ...INSECT_PLAGUE_DEFINITION.terrain }, damage: { formula: `${input.castLevel - 1}d10`, type: 'piercing' },
    expiration: { type: 'rounds', expiresAtRound: input.round + 100, boundary: 'start' } };
};


export const CALL_LIGHTNING_DEFINITION = Object.freeze({
  id: 'spell:call-lightning', name: 'Call Lightning', rulesVersion: '2024', level: 3,
  targeting: Object.freeze({ shape: 'cylinder', radiusFeet: 60, heightFeet: 10, rangeFeet: 120 }),
  durationSeconds: 600, requiresConcentration: true,
  save: Object.freeze({ ability: 'dexterity', triggers: Object.freeze(['selected-strike']) }),
  strike: Object.freeze({ shape: 'sphere', radiusFeet: 5, rangeFeet: 60, actionCost: 'action' }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '3d10', type: 'lightning' }]) }),
});
export const createCallLightningEffect = input => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 3 || input.castLevel > 9 || typeof input.storm !== 'boolean') throw new Error('Invalid Call Lightning slot or storm choice.');
  const { movement, ...base } = createMoonbeamEffect(input);
  return { ...base, definitionId: CALL_LIGHTNING_DEFINITION.id, name: CALL_LIGHTNING_DEFINITION.name,
    area: { ...CALL_LIGHTNING_DEFINITION.targeting }, save: { ...CALL_LIGHTNING_DEFINITION.save, dc: input.dc },
    strike: { ...CALL_LIGHTNING_DEFINITION.strike }, storm: input.storm,
    damage: { formula: `${input.castLevel + (input.storm ? 1 : 0)}d10`, type: 'lightning' },
    expiration: { type: 'rounds', expiresAtRound: input.round + 100, boundary: 'start' } };
};


/** Conjure Woodland Beings: the same aura as Spirit Guardians, for Force damage and no slowing. */
export const CONJURE_WOODLAND_BEINGS_DEFINITION = Object.freeze({
  id: 'spell:conjure-woodland-beings', name: 'Conjure Woodland Beings', rulesVersion: '2024', level: 4,
  targeting: Object.freeze({ shape: 'emanation', radiusFeet: 10, origin: 'self' }),
  durationSeconds: 600, requiresConcentration: true,
  save: Object.freeze({ ability: 'wisdom', triggers: Object.freeze(['first-entry-on-turn', 'area-movement', 'end-of-target-turn']) }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '5d8', type: 'force' }]) }),
});
export const createConjureWoodlandBeingsEffect = input => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 4 || input.castLevel > 9) throw new Error('Invalid Conjure Woodland Beings slot.');
  const { movement, ...base } = createMoonbeamEffect(input);
  return { ...base, definitionId: CONJURE_WOODLAND_BEINGS_DEFINITION.id, name: CONJURE_WOODLAND_BEINGS_DEFINITION.name,
    area: { ...CONJURE_WOODLAND_BEINGS_DEFINITION.targeting, footprintSquares: input.footprintSquares }, followsCaster: true,
    excludedCombatantIds: [...input.excludedCombatantIds], save: { ...CONJURE_WOODLAND_BEINGS_DEFINITION.save, dc: input.dc },
    damage: { formula: `${input.castLevel + 1}d8`, type: 'force' },
    expiration: { type: 'rounds', expiresAtRound: input.round + 100, boundary: 'start' } };
};

export const SPIRIT_GUARDIANS_DEFINITION = Object.freeze({
  id: 'spell:spirit-guardians', name: 'Spirit Guardians', rulesVersion: '2024', level: 3,
  targeting: Object.freeze({ shape: 'emanation', radiusFeet: 15, origin: 'self' }),
  durationSeconds: 600, requiresConcentration: true,
  save: Object.freeze({ ability: 'wisdom', triggers: Object.freeze(['first-entry-on-turn', 'area-movement', 'end-of-target-turn']) }),
  damage: Object.freeze({ components: Object.freeze([{ formula: '3d8', type: 'radiant' }]) }),
});
export const createSpiritGuardiansEffect = input => {
  if (!Number.isInteger(input.castLevel) || input.castLevel < 3 || input.castLevel > 9 || !['radiant', 'necrotic'].includes(input.damageType)) throw new Error('Invalid Spirit Guardians slot or damage type.');
  const { movement, ...base } = createMoonbeamEffect(input);
  return { ...base, definitionId: SPIRIT_GUARDIANS_DEFINITION.id, name: SPIRIT_GUARDIANS_DEFINITION.name,
    area: { ...SPIRIT_GUARDIANS_DEFINITION.targeting, footprintSquares: input.footprintSquares }, followsCaster: true,
    excludedCombatantIds: [...input.excludedCombatantIds], save: { ...SPIRIT_GUARDIANS_DEFINITION.save, dc: input.dc },
    damage: { formula: `${input.castLevel}d8`, type: input.damageType },
    expiration: { type: 'rounds', expiresAtRound: input.round + 100, boundary: 'start' } };
};
